import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { useIssuesStore } from "@/stores/issues";

export function usePreviewLogic() {
  const route = useRoute();
  const router = useRouter();
  const activeDeviceRender = useActiveDeviceRenderStore();
  const issuesStore = useIssuesStore();

  const {
    allAuditIssues,
  } = storeToRefs(issuesStore);

  const issueFilter = ref('all');
  const devicePreview = ref(null);
  const highlightBox = ref(null);
  const highlightDetails = ref({
    content: {
      title: '',
      text_1: '',
      text_2: '',
      url: '',
    },
    style: {
      position: 'absolute',
      top: '0px',
      left: '0px',
      'z-index': '1000',
      visibility: 'hidden',
    },
    index: 0,
    box: 0,
    nextLink: {
      query: {},
      show: false,
      style: {
        color: 'white',
        opacity: 0.8
      }
    },
    prevLink: {
      query: {},
      show: false,
      style: {
        color: 'white',
        opacity: 0.8
      }
    }
  });

  const historyPreview = computed(() => {
    if (activeDeviceRender.historyData) {
      return {
        show: true,
        screenshotsNew: activeDeviceRender.historyData.screenshotsNew,
        screenshotsOld: activeDeviceRender.historyData.screenshotsOld,
      }
    }
    return {
      show: false,
      screenshotsNew: {},
      screenshotsOld: {},
    }
  });

  function hideHighlightDetails(clear = false) {
    if (clear) {
      highlightDetails.value.content = {
        title: '',
        text_1: '',
        text_2: '',
        context: '',
        showContext: false,
      }
    }
    highlightDetails.value.style.visibility = 'hidden'
  }

  function filterIssuesBySeverity(severity, issues = null) {
    if (issues === null) {
      issues = allAuditIssues.value;
    }
    return issues.filter((item) => {
      if (severity === 'all') {
        return true;
      }
      return item.priority === severity
    });
  }

  const filteredIssues = ((issues = null) => {
    return filterIssuesBySeverity(issueFilter.value, issues);
  });

  function showBoxes() {
    if (route.query.section !== 'audit') {
      return
    }
    if (!devicePreview.value) {
      return;
    }

    let issues = filteredIssues();
    let highlightBoxes = []
    let query = {}
    let ratio = devicePreview.value.ratio();

    let activeIssue = issuesStore.getIssueFromQuery(route.query);

    let count = 0;
    for (let issue of issues) {

      let index = 0;

      if (!Array.isArray(issue.occurrences)) {
        continue
      }

      for (let occurrence of issue.occurrences) {
        if (!occurrence) {
          highlightBoxes = []
          return
        }

        let maxTop = (activeDeviceRender.devicePreview.screenshotFull || {}).height || 0
        if (occurrence.box.top > maxTop) {
          continue
        }

        count += 1;
        index += 1;

        let box = {}
        Object.assign(box, occurrence.box);
        let q = {section: 'audit'}
        Object.assign(q, query);
        q['i'] = index;
        q[issue.query] = issue.id;

        if (occurrence.id === 'text_overlap') {
          box.left = Math.min(occurrence.box.left, occurrence.box2.left);
          box.top = Math.min(occurrence.box.top, occurrence.box2.top);
          box.width = Math.max(occurrence.box.left + occurrence.box.width, occurrence.box2.left + occurrence.box2.width) - box.left;
          box.height = Math.max(occurrence.box.top + occurrence.box.height, occurrence.box2.top + occurrence.box2.height) - box.top;
        }
        if (box.width < 12) {
          box.left -= 6;
          box.top -= 6;
          box.width += 12;
          box.height += 12;
        }


        let variant = {
          'type': 'box',
          'color': 'red',
          'sentiment': 'negative',
        }
        if (occurrence.variant) {
          variant = occurrence.variant;
        }

        let id = `highlight-box-inactive-${count}`
        if (activeIssue && activeIssue.id === issue.id) {
          id = `highlight-box-${index}`
        }

        highlightBoxes.push({
          'id': id,
          'key': id,
          'i': `${index}`,
          'highlight': false,
          'priority': issue.priority,
          'active': activeIssue && activeIssue.id === issue.id,
          'sentiment': variant.sentiment,
          'box': box,
          'style': {
            'visibility': 'visible',
            'left': `${box.left * ratio}px`,
            'top': `${box.top * ratio}px`,
            'width': `${box.width * ratio}px`,
            'height': `${box.height * ratio}px`,
          },
          'query': q,
          'variant': variant
        })
      }
    }
    devicePreview.value.showBoxes(highlightBoxes)
  }

  function highlightEntry(entry) {
    let index = parseInt(route.query.i, 10) || 1;

    if (!entry.occurrences || !Array.isArray(entry.occurrences)) {
      return
    }

    let occurrences = JSON.parse(JSON.stringify(entry.occurrences))
    let occurrence = entry.occurrences[index - 1];

    if (!occurrence) {
      return
    }

    let query = {section: 'audit'}
    query[entry.query] = entry.id;
    occurrences.sort((a, b) => {
      if (a.box.top > b.box.top) {
        return 1
      }
      if (a.box.top < b.box.top) {
        return -1
      }
      return 0
    })
    highlightDetails.value.nextLink.query = {};
    highlightDetails.value.prevLink.query = {};
    Object.assign(highlightDetails.value.nextLink.query, query);
    Object.assign(highlightDetails.value.prevLink.query, query);
    highlightDetails.value.nextLink.query.i = index + 1
    highlightDetails.value.prevLink.query.i = index - 1
    highlightDetails.value.nextLink.show = true
    highlightDetails.value.prevLink.show = true
    if (index === 1) {
      highlightDetails.value.prevLink.show = false
    }
    if (index === occurrences.length) {
      highlightDetails.value.nextLink.show = false
    }

    highlightDetails.value.content = occurrence.highlightDetails;
    showBoxes()
    devicePreview.value.scrollToBox(occurrence.box)
  }

  function scrollToIssue() {
    highlightDetails.value.style.visibility = 'hidden'
    let issue = issuesStore.getIssueFromQuery(route.query);

    if (issue) {
      highlightEntry(issue)
    }
  }

  function openIssue() {
    setTimeout(() => {
      let activeIssue = issuesStore.getIssueFromQuery(route.query);

      if (activeIssue) {
        const id = `details_copy_${activeIssue.query}`;
        const el = document.getElementById(id);
        if (!el) return;

        // Avoid auto-toggling on init; we'll control it with show()
        const collapse = window.bootstrap.Collapse.getOrCreateInstance(el, {toggle: false});
        collapse.show();
      }
    }, 100);
  }

  function openAuditIssue() {
    if (!route.query.section !== 'audit') {
      openIssue();
    }
  }

  function onLoaded(options) {
    if (route.query.focus) {
      let issue = issuesStore.getIssueFromQuery(route.query)
      if (issue) {
        let q = route.query;
        q[issue.query] = issue.id;
        q.i = 1;
        delete q['focus'];
        delete q['focus_priority'];
        router.replace({query: q, force: true});
        return
      }
    }

    showBoxes()
    scrollToIssue()
    openAuditIssue()
  }

  function scroll(dir) {
    hideHighlightDetails();
    devicePreview.value.scroll(dir)
  }

  function onManualScroll(scrollPosition) {
    hideHighlightDetails();
  }

  function onScrollToEntryCompleted() {
    let query = route.query;
    let index = parseInt(query.i, 10) || 0;
    if (highlightDetails.value.content.title) {
      highlightBox.value.showHighlightDetails()
    }
  }

  function logClick(event) {
    highlightBox.value.scrollFunction()
  }

  watch(() => route.query, (newQ, oldQ) => {
    hideHighlightDetails(true)
    if (newQ.section !== oldQ.section) {
      devicePreview.value.showBoxes([])
      devicePreview.value.setAnnotations([]);
    }
    showBoxes()
    scrollToIssue()
    openAuditIssue()
  });

  return {
    // Refs
    issueFilter,
    devicePreview,
    highlightBox,
    highlightDetails,

    // Computed
    historyPreview,

    // Functions
    hideHighlightDetails,
    filterIssuesBySeverity,
    filteredIssues,
    showBoxes,
    highlightEntry,
    scrollToIssue,
    openIssue,
    openAuditIssue,
    onLoaded,
    scroll,
    onManualScroll,
    onScrollToEntryCompleted,
    logClick,

    // Stores
    activeDeviceRender,
    issuesStore,
    route,
    router,
  }
}
